/**
 * Field intel (what a deputy saw that might matter later), the investigator's case workspace,
 * and case activity for command staff.
 */
import { useState, type FormEvent } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Icon } from '../shared/icons.tsx';
import { Badge, Card, ErrorBox, FileDrop, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, KPI, when } from './common.tsx';

const CATEGORIES = ['Suspicious vehicle', 'Suspicious person', 'Suspicious activity', 'Known associates together', 'Possible drug activity', 'Gang / graffiti', 'Property / location of interest', 'Other'];
const carText = (v: any) => [v.color, v.year, v.make, v.model].filter(Boolean).join(' ') + (v.plate ? ` · ${v.state ? `${v.state} ` : ''}${v.plate}` : '') + (v.description ? ` · ${v.description}` : '');
const personText = (p: any) => [p.name, p.dob && `DOB ${p.dob}`, p.description].filter(Boolean).join(' · ');
const localNow = () => {
  const d = new Date();
  return new Date(+d - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

// ─── Field intel ────────────────────────────────────────────────────────────
export function IntelPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState(new URLSearchParams(location.search).get('q') ?? '');
  const [query, setQuery] = useState(q);
  const [mine, setMine] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/intel?q=${encodeURIComponent(query)}${mine ? '&mine=1' : ''}`), [query, mine]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Records"
        title="Field intel"
        sub="Things deputies saw that didn’t need a report — yet. Search by tag, name, place or any words."
        actions={
          <button className="btn primary" onClick={() => navigate(at('/intel/new'))}>
            <Icon name="plus" size={16} /> Log something I saw
          </button>
        }
      />
      <form
        className="row gap wrap"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(q.trim());
        }}
      >
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tag, name, place, “white van”…" aria-label="Search field intel" style={{ flex: 1 }} />
        <button className="btn">Search</button>
        <label className="check">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only mine
        </label>
      </form>
      {r.error ? <ErrorBox error={r.error} /> : !r.data ? <Spinner /> : !r.data.entries.length ? <EmptyState icon="search" title={query ? 'Nothing matches' : 'Nothing logged yet'} /> : <IntelList entries={r.data.entries} />}
    </div>
  );
}

function IntelList({ entries }: { entries: any[] }) {
  const { navigate } = useRouter();
  return (
    <div className="fi-list">
      {entries.map((f) => (
        <button key={f.id} type="button" className="card fi-item" onClick={() => navigate(at(`/intel/${f.id}`))}>
          <div className="fi-top">
            <strong>{f.category}</strong>
            <span className="small muted">
              {when(f.observed_at)} · {f.by_name}
            </span>
          </div>
          {f.location && <div className="small">{f.location}</div>}
          <div className="fi-what">{f.what}</div>
          {(f.vehicles?.length > 0 || f.people?.length > 0) && (
            <div className="fi-tags">
              {f.vehicles?.map((v: any, i: number) => (
                <span key={`v${i}`} className="fi-tag">
                  <Icon name="car" size={13} /> {carText(v)}
                </span>
              ))}
              {f.people?.map((p: any, i: number) => (
                <span key={`p${i}`} className="fi-tag">
                  <Icon name="user" size={13} /> {personText(p)}
                </span>
              ))}
            </div>
          )}
          <div className="small muted">
            {f.number}
            {f.photos ? ` · ${f.photos} photo${f.photos > 1 ? 's' : ''}` : ''}
            {f.case_number ? ` · case ${f.case_number}` : ''}
          </div>
        </button>
      ))}
    </div>
  );
}

export function NewIntel() {
  const { navigate } = useRouter();
  const [f, setF] = useState({ category: 'Suspicious vehicle', observedAt: localNow(), location: '', what: '', caseNumber: '' });
  const [geo, setGeo] = useState<{ lat: number; lng: number } | null>(null);
  const [geoMsg, setGeoMsg] = useState('');
  const [vehicles, setVehicles] = useState([{ plate: '', state: 'SC', color: '', make: '', model: '' }]);
  const [people, setPeople] = useState<{ name: string; description: string }[]>([]);
  const [photo, setPhoto] = useState<File | null>(null);
  const a = useAction();
  const here = () => {
    if (!navigator.geolocation) return setGeoMsg('This device can’t share its location.');
    setGeoMsg('Finding you…');
    navigator.geolocation.getCurrentPosition(
      (p) => (setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }), setGeoMsg(`GPS ${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}`)),
      () => setGeoMsg('Couldn’t get your location.'),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      const r = await api.post<any>('/api/intel', {
        ...f,
        observedAt: f.observedAt ? new Date(f.observedAt).toISOString() : null,
        caseNumber: f.caseNumber || null,
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
        vehicles: vehicles.filter((v) => v.plate || v.make || v.color),
        people: people.filter((p) => p.name || p.description),
      });
      if (photo) await api.post(`/api/intel/${r.id}/photos`, { imageBase64: await fileToBase64(photo) });
      navigate(at(`/intel/${r.id}`), true);
    });
  };
  const setV = (i: number, k: string, v: string) => setVehicles(vehicles.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setP = (i: number, k: string, v: string) => setPeople(people.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="ops stack">
      <PageHeader crumb={<button className="btn link" onClick={() => navigate(at('/intel'))}>Field intel</button>} title="Log something I saw" sub="No report needed. It’s saved so anyone can find it later by tag, name or place." />
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={a.error} />
        <Card>
          <div className="stack">
            <div className="grid-3">
              <label>
                What kind
                <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                  {CATEGORIES.map((c: string) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                When
                <input type="datetime-local" value={f.observedAt} onChange={(e) => setF({ ...f, observedAt: e.target.value })} />
              </label>
              <label>
                Where
                <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Old mill site, Mill Rd" />
              </label>
            </div>
            <div className="row gap wrap">
              <button type="button" className="btn small" onClick={here}>
                <Icon name="map" size={14} /> Use my location
              </button>
              {geoMsg && <span className="small muted">{geoMsg}</span>}
            </div>
            <label>
              What you saw *
              <textarea rows={4} required value={f.what} onChange={(e) => setF({ ...f, what: e.target.value })} placeholder="White cargo van parked behind the old mill at 0130, two men loading something…" />
            </label>
          </div>
        </Card>
        <Card title="Vehicles">
          <div className="stack sm">
            {vehicles.map((v, i) => (
              <div key={i} className="grid-4 fi-row">
                <label>
                  Tag
                  <input value={v.plate} onChange={(e) => setV(i, 'plate', e.target.value.toUpperCase())} />
                </label>
                <label>
                  State
                  <input value={v.state} maxLength={2} onChange={(e) => setV(i, 'state', e.target.value.toUpperCase())} />
                </label>
                <label>
                  Color
                  <input value={v.color} onChange={(e) => setV(i, 'color', e.target.value)} />
                </label>
                <label>
                  Make / model
                  <input value={`${v.make}${v.model ? ` ${v.model}` : ''}`} onChange={(e) => {
                    const [mk, ...md] = e.target.value.split(' ');
                    setVehicles(vehicles.map((x, j) => (j === i ? { ...x, make: mk ?? '', model: md.join(' ') } : x)));
                  }} />
                </label>
              </div>
            ))}
            <div>
              <button type="button" className="btn small" onClick={() => setVehicles([...vehicles, { plate: '', state: 'SC', color: '', make: '', model: '' }])}>
                + Another vehicle
              </button>
            </div>
          </div>
        </Card>
        <Card title="People">
          <div className="stack sm">
            {people.map((p, i) => (
              <div key={i} className="grid-2 fi-row">
                <label>
                  Name (if known)
                  <input value={p.name} onChange={(e) => setP(i, 'name', e.target.value)} />
                </label>
                <label>
                  Description
                  <input value={p.description} onChange={(e) => setP(i, 'description', e.target.value)} placeholder="W/M, beard, orange hat" />
                </label>
              </div>
            ))}
            <div>
              <button type="button" className="btn small" onClick={() => setPeople([...people, { name: '', description: '' }])}>
                + Add a person
              </button>
            </div>
          </div>
        </Card>
        <Card title="Photo (optional)">
          <FileDrop accept="image/jpeg,image/png" file={photo} onFile={setPhoto} label="Photo" hint="JPEG or PNG" />
        </Card>
        <label>
          Case # (if it’s about one)
          <input value={f.caseNumber} onChange={(e) => setF({ ...f, caseNumber: e.target.value })} />
        </label>
        <div>
          <button className="btn primary" disabled={a.busy}>
            {a.busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function IntelDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/intel/${id}`), [id]);
  const a = useAction();
  const [caseNo, setCaseNo] = useState<string | null>(null);
  const v = r.data;
  if (!v) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const f = v.entry;
  const remove = () => {
    const reason = prompt('Why remove it? (It’s kept on record, just hidden.)');
    if (reason) a.run(async () => (await api.post(`/api/intel/${id}/remove`, { reason }), navigate(at('/intel'), true)));
  };
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<button className="btn link" onClick={() => navigate(at('/intel'))}>Field intel</button>}
        title={`${f.number} · ${f.category}`}
        sub={`${when(f.observed_at)} · ${f.by_name}${f.by_call ? ` (${f.by_call})` : ''}`}
        actions={
          v.canRemove && (
            <button className="btn" onClick={remove}>
              Remove
            </button>
          )
        }
      />
      <ErrorBox error={a.error} />
      <Card>
        <dl className="kv">
          <dt>Where</dt>
          <dd>
            {f.location ?? '—'}
            {f.lat != null && (
              <div className="small">
                <a href={`https://www.google.com/maps?q=${f.lat},${f.lng}`} target="_blank" rel="noreferrer">
                  GPS {Number(f.lat).toFixed(5)}, {Number(f.lng).toFixed(5)}
                </a>
              </div>
            )}
          </dd>
          <dt>What they saw</dt>
          <dd className="pre">{f.what}</dd>
          {f.vehicles.length > 0 && (
            <>
              <dt>Vehicles</dt>
              <dd>
                {f.vehicles.map((x: any, i: number) => (
                  <div key={i}>{carText(x)}</div>
                ))}
              </dd>
            </>
          )}
          {f.people.length > 0 && (
            <>
              <dt>People</dt>
              <dd>
                {f.people.map((x: any, i: number) => (
                  <div key={i}>{personText(x)}</div>
                ))}
              </dd>
            </>
          )}
          <dt>Case #</dt>
          <dd>
            <div className="row gap">
              <input value={caseNo ?? f.case_number ?? ''} onChange={(e) => setCaseNo(e.target.value)} aria-label="Case number" style={{ maxWidth: 180 }} />
              <button className="btn small" disabled={a.busy || caseNo === null} onClick={() => a.run(async () => (await api.post(`/api/intel/${id}/case`, { caseNumber: caseNo || null }), setCaseNo(null), r.reload()))}>
                Save
              </button>
            </div>
          </dd>
        </dl>
        {v.photos.length > 0 && (
          <div className="fi-photos">
            {v.photos.map((p: string) => (
              <a key={p} href={`/api/intel/photos/${p}`} target="_blank" rel="noreferrer">
                <img src={`/api/intel/photos/${p}`} alt="Photo from the scene" />
              </a>
            ))}
          </div>
        )}
      </Card>
      {v.related.length > 0 && (
        <>
          <h2 className="tr-h">Same tag or person, other times</h2>
          <IntelList entries={v.related} />
        </>
      )}
    </div>
  );
}

// ─── Investigator's workspace (on the case page) ─────────────────────────────
const KINDS: [string, string, string][] = [
  ['note', 'Note', 'pen'],
  ['person', 'Person', 'user'],
  ['phone', 'Phone number', 'phone'],
  ['vehicle', 'Vehicle', 'car'],
  ['location', 'Place', 'map'],
  ['event', 'Timeline event', 'calendar'],
  ['photo', 'Photo', 'upload'],
  ['file', 'File', 'folder'],
];

export function CaseWorkspace({ id, onChange }: { id: string; onChange: () => void }) {
  const r = useLoad(() => api.get<any>(`/api/cases/${id}/workspace`), [id]);
  const [kind, setKind] = useState<string | null>(null);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : null;
  const groups = KINDS.map(([k, label, icon]) => ({ k, label, icon, items: r.data.entries.filter((e: any) => e.kind === k) })).filter((g) => g.items.length);
  const remove = (eid: string) => confirm('Remove this from the case? (Kept on record, hidden.)') && a.run(async () => (await api.post(`/api/cases/entries/${eid}/remove`, {}), r.reload(), onChange()));
  return (
    <Card title="Case workspace" actions={r.data.canAdd && <span className="small muted">Add notes, names, numbers and photos — they go on the timeline.</span>}>
      <div className="stack">
        {r.data.canAdd && (
          <div className="cw-add">
            {KINDS.map(([k, label, icon]) => (
              <button key={k} type="button" className={`btn small${kind === k ? ' primary' : ''}`} onClick={() => setKind(kind === k ? null : k)}>
                <Icon name={icon} size={14} /> {label}
              </button>
            ))}
          </div>
        )}
        {kind && <AddEntry id={id} kind={kind} onDone={() => (setKind(null), r.reload(), onChange())} />}
        <ErrorBox error={a.error} />
        {!groups.length && !r.data.intel.length ? (
          <p className="small muted">Nothing added yet.</p>
        ) : (
          <div className="cw-groups">
            {groups.map((g) => (
              <section key={g.k} className="cw-group">
                <h3>
                  <Icon name={g.icon} size={15} /> {g.label === 'Person' ? 'People' : `${g.label}s`}
                </h3>
                <ul>
                  {g.items.map((e: any) => (
                    <li key={e.id}>
                      <div>
                        <strong>{e.has_file ? <a href={`/api/cases/entries/${e.id}/file`} target="_blank" rel="noreferrer">{e.title}</a> : e.title}</strong>
                        {Object.keys(e.data ?? {}).length > 0 && <div className="small">{Object.entries(e.data).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>}
                        {e.body && <div className="small pre">{e.body}</div>}
                        <div className="small muted">
                          {e.occurred_at ? `${when(e.occurred_at)} · ` : ''}added by {e.by_name}, {when(e.created_at)}
                        </div>
                        {e.kind === 'photo' && e.has_file && <img className="cw-thumb" src={`/api/cases/entries/${e.id}/file`} alt={e.title} />}
                      </div>
                      {r.data.canAdd && (
                        <button type="button" className="btn link small" onClick={() => remove(e.id)}>
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {r.data.intel.length > 0 && (
              <section className="cw-group">
                <h3>
                  <Icon name="search" size={15} /> Field intel
                </h3>
                <ul>
                  {r.data.intel.map((f: any) => (
                    <li key={f.id}>
                      <div>
                        <strong>
                          {f.number} · {f.category}
                        </strong>
                        <div className="small">{[f.location, f.what].filter(Boolean).join(' · ')}</div>
                        <div className="small muted">
                          {when(f.observed_at)} · {f.by_name}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function AddEntry({ id, kind, onDone }: { id: string; kind: string; onDone: () => void }) {
  const [f, setF] = useState<Record<string, string>>({ title: '', body: '', occurredAt: '' });
  const [file, setFile] = useState<File | null>(null);
  const a = useAction();
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const DATA: Record<string, [string, string][]> = {
    person: [['name', 'Name'], ['role', 'Role (witness, suspect…)'], ['dob', 'DOB'], ['phone', 'Phone'], ['address', 'Address']],
    phone: [['phone', 'Number'], ['owner', 'Whose'], ['carrier', 'Carrier']],
    vehicle: [['plate', 'Tag'], ['state', 'State'], ['color', 'Color'], ['make', 'Make'], ['model', 'Model'], ['owner', 'Owner']],
    location: [['address', 'Address']],
  };
  const titleLabel: Record<string, string> = { note: 'Note', person: 'Who they are to the case', phone: 'What the number is', vehicle: 'What the vehicle is', location: 'What the place is', event: 'What happened', photo: 'What it shows', file: 'What it is' };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      const data = Object.fromEntries((DATA[kind] ?? []).map(([k]) => [k, f[k] ?? '']).filter(([, v]) => v));
      await api.post(`/api/cases/${id}/entries`, {
        kind,
        title: f.title || (data as any).name || (data as any).phone || (data as any).plate || (kind === 'note' ? (f.body ?? '').slice(0, 60) : ''),
        body: f.body || null,
        occurredAt: f.occurredAt ? new Date(f.occurredAt).toISOString() : null,
        data,
        ...(file ? { fileBase64: await fileToBase64(file), filename: file.name } : {}),
      });
      onDone();
    });
  };
  return (
    <form className="cw-form stack sm" onSubmit={submit}>
      <ErrorBox error={a.error} />
      {(DATA[kind] ?? []).length > 0 && (
        <div className="grid-3">
          {DATA[kind]!.map(([k, l]) => (
            <label key={k}>
              {l}
              <input value={f[k] ?? ''} onChange={set(k)} />
            </label>
          ))}
        </div>
      )}
      {(kind === 'photo' || kind === 'file') && <FileDrop accept={kind === 'photo' ? 'image/jpeg,image/png' : undefined} file={file} onFile={setFile} label={kind === 'photo' ? 'Photo' : 'File'} />}
      <div className="grid-2">
        <label>
          {titleLabel[kind]}
          <input value={f.title} onChange={set('title')} required={kind !== 'note' && !(DATA[kind] ?? []).length} />
        </label>
        <label>
          When it happened (puts it on the timeline there)
          <input type="datetime-local" value={f.occurredAt} onChange={set('occurredAt')} />
        </label>
      </div>
      <label>
        {kind === 'note' ? 'Note' : 'Details'}
        <textarea rows={kind === 'note' ? 4 : 2} value={f.body} onChange={set('body')} required={kind === 'note'} />
      </label>
      <div>
        <button className="btn primary" disabled={a.busy}>
          Add to the case
        </button>
      </div>
    </form>
  );
}

// ─── Case activity for command staff ────────────────────────────────────────
export function CaseActivity() {
  const { navigate } = useRouter();
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const [inv, setInv] = useState('');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/cases/activity?scope=${scope}&investigator=${inv}&q=${encodeURIComponent(q.trim())}`), [scope, inv, q]);
  const tone = (d: number | null) => (d == null || d >= 60 ? 'bad' : d >= 30 ? 'warn' : 'ok');
  return (
    <div className="ops stack">
      <PageHeader crumb="Investigations" title="Case activity" sub="Every case and the last thing that happened on it. Longest without action first." />
      {r.data && (
        <div className="ops-kpis">
          <KPI n={r.data.counts.total} label={scope === 'open' ? 'Open cases' : 'Cases'} />
          <KPI n={r.data.counts.over30} label="No action 30+ days" tone={r.data.counts.over30 ? 'warn' : undefined} />
          <KPI n={r.data.counts.over60} label="No action 60+ days" tone={r.data.counts.over60 ? 'bad' : undefined} />
          <KPI n={r.data.counts.unassigned} label="No investigator" onClick={() => setInv(inv === 'none' ? '' : 'none')} on={inv === 'none'} />
        </div>
      )}
      <div className="row gap wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which cases">
          {(['open', 'all'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k} className={scope === k ? 'on' : ''} onClick={() => setScope(k)}>
              {k === 'open' ? 'Open' : 'All'}
            </button>
          ))}
        </div>
        <select value={inv} onChange={(e) => setInv(e.target.value)} aria-label="Investigator">
          <option value="">Every investigator</option>
          <option value="none">No investigator</option>
          {(r.data?.investigators ?? []).map((x: any) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case #, name…" aria-label="Search cases" />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.cases.length ? (
        <EmptyState icon="folder" title="No cases" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Case #</th>
                <th>Investigator</th>
                <th>Status</th>
                <th>Last action</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {r.data.cases.map((c: any) => (
                <tr key={c.id} className="clickable" onClick={() => navigate(at(`/cases/${c.id}`))}>
                  <td>
                    <strong className="mono">{c.case_number}</strong>
                    <div className="small muted">by {c.author_name}</div>
                  </td>
                  <td>{c.investigator_name ?? <span className="muted">—</span>}</td>
                  <td>{c.caseStatusLabel ?? (c.status === 'submitted' ? 'Report awaiting approval' : 'Report approved')}</td>
                  <td>
                    {c.last_what ?? '—'}
                    {c.last_by && <div className="small muted">{c.last_by}</div>}
                  </td>
                  <td>
                    {c.last_at ? when(c.last_at, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    <div>
                      <Badge tone={tone(c.daysSince)}>{c.daysSince == null ? 'No action' : c.daysSince === 0 ? 'Today' : `${c.daysSince} day${c.daysSince === 1 ? '' : 's'} ago`}</Badge>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Notice>Last action counts report changes, investigator updates, workspace notes, evidence moves, supplements and files sent to the solicitor.</Notice>
    </div>
  );
}
